// The batches one batch-tracked item holds, as read-only rows. Nothing here is typed
// in: a quantity changes only through a stock operation that records why — receiving,
// moving, setting aside, a kit check, a drug given — so each row offers those instead.
import axios from "axios";
import {
  Box, Button, Chip, IconButton, Menu, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HistoryIcon from "@mui/icons-material/History";
import { useState } from "react";
import { API_BASE_URL } from "../../config";
import { notify } from "../../notify";
import { expiryFlag } from "../../stockRules";

export default function HoldingsPanel({ item, grace = 0, required, canMove, canHistory,
                                        onMove, onQuarantine, onHistory, onChanged }) {
  const [menu, setMenu] = useState(null); // { anchor, holding }
  const holdings = item?.batches || [];
  const total = holdings.reduce((n, h) => n + (h.quantity || 0), 0);
  const want = required == null || required === "" ? null : Number(required);
  const short = want != null && total < want;

  const openUnit = (h, action) => {
    setMenu(null);
    axios.post(`${API_BASE_URL}/stock/open`, { holding_id: h.id, action })
      .then(() => {
        notify(action === "open" ? "Marked one unit as opened." : "Opened unit taken out of stock.",
               "success");
        onChanged && onChanged();
      }).catch(() => {});
  };

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Batches held</Typography>
        <Typography variant="body2" sx={short ? { color: "error.main", fontWeight: 600 } : undefined}>
          {want == null ? `Total ${total}`
            : short ? `Total ${total} — ${want - total} short of ${want} required`
            : `Total ${total} of ${want} required`}
        </Typography>
      </Stack>
      {grace > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Batches of this type are held up to {grace} year{grace === 1 ? "" : "s"} past the
          date printed on them before they're flagged as expired.
        </Typography>
      )}
      {holdings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None held here. Stock arrives by booking in a delivery to a stock cupboard, or by
          moving it here from somewhere else.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700 } }}>
              <TableCell>Batch</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right" sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {holdings.map(h => {
              const flag = expiryFlag(h.expiry_date, grace);
              return (
                <TableRow key={h.id}>
                  <TableCell>
                    {h.serial_number || <Typography component="span" variant="body2" color="text.secondary">Not recorded</Typography>}
                    {h.recalled && <Chip size="small" color="error" label="Recalled" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={flag?.message || ""}>
                      <Box component="span"
                           sx={flag ? { color: `${flag.severity}.main`, fontWeight: 600 } : undefined}>
                        {h.expiry_date || "—"}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {h.quantity}
                    {h.opened > 0 && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {" "}({h.opened} opened)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {(canMove || canHistory) && (
                      <IconButton size="small" aria-label="Batch actions"
                                  onClick={e => setMenu({ anchor: e.currentTarget, holding: h })}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {canHistory && item?.id && (
        <Button size="small" startIcon={<HistoryIcon />} sx={{ mt: 1 }} onClick={() => onHistory({ item })}>
          History of this item
        </Button>
      )}
      <Menu open={Boolean(menu)} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {canMove && (
          <MenuItem onClick={() => { const h = menu.holding; setMenu(null); onMove(h); }}>Move…</MenuItem>
        )}
        {canMove && (
          <MenuItem onClick={() => { const h = menu.holding; setMenu(null); onQuarantine(h); }}>
            Set aside for disposal…
          </MenuItem>
        )}
        {canMove && menu?.holding && menu.holding.opened < menu.holding.quantity && (
          <MenuItem onClick={() => openUnit(menu.holding, "open")}>Mark one unit opened</MenuItem>
        )}
        {canMove && menu?.holding?.opened > 0 && (
          <MenuItem onClick={() => openUnit(menu.holding, "finish")}>Opened unit used up</MenuItem>
        )}
        {canHistory && (
          <MenuItem onClick={() => { const h = menu.holding; setMenu(null); onHistory({ batch: h }); }}>
            History of this batch
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
